import { getIdToken } from './firebase';
import type {
  AdminUser,
  Booking,
  BookingPaymentType,
  TableEmbed,
  CoachSeries,
  CoachSeriesDetail,
  OneTimeCoachBooking,
  AdminLookupUser,
  SubscriptionWithUsage,
  SubscriptionPlan,
  AdminTable,
  Paged,
  RevenueStats,
  BookingStats,
  OccupancyStats,
  MemberStats,
  SlotConflictDetail,
  SeriesOccurrenceConflictDetail,
  SeriesTimeShiftResult,
  SeriesTableTransferResult,
  AdminTournament,
  AdminParticipant,
  ParticipantRecord,
  AdminTournamentSeries,
  AdminTournamentSeriesCreateResponse,
  AdminTournamentSeriesDetailResponse,
  AdminTournamentSeriesEditResponse,
  AdminVendingProduct,
  VendingPurchasesResponse,
  TtarTournamentSyncResponse,
  AdminTtarLinkBody,
  TtarLinkState,
  AdminSalesResponse,
  SalesKind,
  CreateTournamentYoutubeBroadcastOptions,
  AdminShareLink,
} from './types';
import type { components, operations } from './generated/schema';

// Request bodies backed by the generated OpenAPI types (the contract). The
// `satisfies` at the call sites below fail `npm run typecheck` if a payload
// drifts from the spec. Regenerate with `npm run gen:api` after a contract change.
type UpdateBookingTimesBody = components['schemas']['UpdateBookingTimesRequest'];
type TransferBookingBody = components['schemas']['TransferBookingRequest'];
type ToggleCoachBody = components['schemas']['ToggleCoachRequest'];
type OpeningHoursPutBody = components['schemas']['OpeningHoursPut'];
export type OpeningHoursDay = components['schemas']['OpeningHoursDayIn'] & {
  /** Derived server-side so the editor can describe an overnight closing. */
  closes_next_day?: boolean;
};
export interface OpeningHours {
  timezone: string;
  days: OpeningHoursDay[];
  updated_at: string | null;
  updated_by: number | null;
  affected_bookings?: AffectedBooking[];
}
export type MaintenanceWindow = components['schemas']['MaintenanceWindowIn'] & {
  id: number;
  created_at: string;
  created_by: number | null;
};
export type AffectedBooking = {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  table_id: number;
  table_name: string;
  payment_type: string;
  owner_name: string | null;
  reason: 'outside_opening_hours' | 'maintenance_window';
};
export type CreateMaintenanceWindowBody = components['schemas']['MaintenanceWindowIn'];
export type ShiftCoachSeriesTimeBody = components['schemas']['ShiftCoachSeriesTimeRequest'];
export type TransferCoachSeriesTableBody =
  components['schemas']['TransferCoachSeriesTableRequest'];
export type CreateTournamentBody = components['schemas']['CreateTournamentRequest'];
// The API models this field with a default yet reads patches with
// `exclude_unset=True`; a minimal PATCH can therefore omit every field.
export type PatchTournamentBody = Partial<components['schemas']['PatchTournamentRequest']>;
export type CreateTournamentSeriesBody = components['schemas']['CreateTournamentSeriesRequest'];
export type PatchTournamentSeriesBody = Partial<components['schemas']['PatchTournamentSeriesRequest']>;
export type EndTournamentSeriesBody = components['schemas']['EndTournamentSeriesRequest'];
type SetClimateSwitchBody = components['schemas']['SetSwitchRequest'];
export type ClimateSnapshot = components['schemas']['ClimateSnapshot'];
export type ClimateSwitchState = components['schemas']['SwitchState'];
export type StreamCamera = components['schemas']['StreamCamera'];
export type StreamsResponse = components['schemas']['StreamsResponse'];
export type StreamActionResponse = components['schemas']['StreamActionResponse'];
export type StopAllStreamsResponse = components['schemas']['StopAllStreamsResponse'];
export type NvrCamera = components['schemas']['NvrCamera'];
export type NvrCamerasResponse = components['schemas']['NvrCamerasResponse'];
export type YoutubeIntegrationReadiness = components['schemas']['YoutubeIntegrationReadiness'];
export type YoutubeBroadcastPreview = components['schemas']['YoutubeBroadcastPreview'];
export type YoutubeBroadcastResult = components['schemas']['YoutubeBroadcastResult'];
export type YoutubeBroadcastTableOption = components['schemas']['YoutubeBroadcastTableOption'];
export type TournamentYoutubeBroadcastsResponse = components['schemas']['TournamentYoutubeBroadcastsResponse'];
export type CreateVendingProductBody = components['schemas']['CreateVendingProductRequest'];
export type PatchVendingProductBody = components['schemas']['PatchVendingProductRequest'];
type ApproveDisplayPairingBody = components['schemas']['ApproveDisplayPairingRequest'];
type UpdateDisplayDeviceBody = components['schemas']['UpdateDisplayDeviceRequest'];
export type DisplayDevice = components['schemas']['DisplayDevice'];
export type CoachProfile = components['schemas']['CoachProfile'];
export type CoachProfileSaveBody = components['schemas']['CoachProfileSaveRequest'];
export type CoachProfilesResponse = components['schemas']['CoachProfilesResponse'];
export type CoachCallback = components['schemas']['CoachCallback'];
export type CoachCallbacksResponse = components['schemas']['CoachCallbacksResponse'];
export type CoachCallbackContactBody = components['schemas']['CoachCallbackContactUpdate'];
export type CoachCallbackListParams = NonNullable<
  operations['list_callbacks_admin_coach_callbacks_get']['parameters']['query']
>;

const BASE_URL = import.meta.env.VITE_API_URL;
if (!BASE_URL) {
  // Hard-fail on missing config — the alternative is silent same-origin
  // requests that 404, which we'd waste time debugging.
  throw new Error('VITE_API_URL is not set');
}

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;
  constructor(status: number, message: string, code?: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/**
 * Best-effort human-readable message out of an API error body's `detail`.
 *
 * The API emits several shapes: a plain string on most endpoints, a structured
 * object for slot conflicts and coach-series conflicts (api#81 unified 17 call
 * sites onto `{error, code, conflicts, next_free_time}`), and an array of
 * `{loc, msg, type}` for FastAPI validation failures. Returns undefined when
 * there is nothing worth showing, so the caller can fall back to the status
 * line — that is better than an empty toast.
 */
function messageFromDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail.trim() ? detail : undefined;
  if (!isRecord(detail)) return undefined; // includes the 422 array
  // `error` first: every ProSpin error body uses it. `message` is the
  // defensive second guess, matching prospin-web's client.ts.
  if (typeof detail.error === 'string' && detail.error.trim()) return detail.error;
  if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
  return undefined;
}

function pickCode(x: unknown): string | undefined {
  return isRecord(x) && typeof x.code === 'string' ? x.code : undefined;
}

/**
 * Narrow a caught error to the API's structured slot-conflict payload, or null.
 *
 * Lets a caller render *which* bookings hold the slot and when it frees up
 * without casting `ApiError.detail` by hand at every call site:
 *
 *     const conflict = slotConflictDetail(err);
 *     if (conflict) { … conflict.conflicts, conflict.next_free_time … }
 */
export function slotConflictDetail(err: unknown): SlotConflictDetail | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.detail;
  if (!isRecord(d) || !Array.isArray(d.conflicts)) return null;
  if (typeof d.error !== 'string') return null;
  // An empty list is evidence of neither shape — and `SlotConflictBanner`
  // renders nothing for it anyway — so drop it rather than let this narrowing
  // claim it by default (the series check below can only fire on `length > 0`).
  // Callers fall back to `ApiError.message`, which is still the API's sentence.
  if (d.conflicts.length === 0) return null;
  // A series-wide edit's 409 (api#84/#85) also carries `error` + `conflicts[]`,
  // but each entry there is a *date* wrapping its own occupancy list rather
  // than an occupancy itself. Accepting it would hand SlotConflictBanner
  // entries with no `kind`/`booking_id` and print "undefined · undefined";
  // `seriesOccurrenceConflictDetail` is the narrowing for that body.
  if (looksLikeSeriesConflicts(d.conflicts)) return null;
  return d as unknown as SlotConflictDetail;
}

/** True when every entry nests an `occupied_by` list — the series-edit shape. */
function looksLikeSeriesConflicts(conflicts: unknown[]): boolean {
  return (
    conflicts.length > 0 &&
    conflicts.every((c) => isRecord(c) && Array.isArray(c.occupied_by))
  );
}

/**
 * Narrow a caught error to the per-date payload a rejected series-wide edit
 * returns (api#84's time shift, api#85's table transfer), or null.
 *
 * Narrowed by *shape*, not by `code`: the API gives both endpoints an
 * identical body so one renderer covers both, and keying on
 * `series_time_conflicts` would silently drop the other one.
 */
export function seriesOccurrenceConflictDetail(
  err: unknown,
): SeriesOccurrenceConflictDetail | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.detail;
  if (!isRecord(d) || typeof d.error !== 'string') return null;
  if (!Array.isArray(d.conflicts) || !looksLikeSeriesConflicts(d.conflicts)) return null;
  return d as unknown as SeriesOccurrenceConflictDetail;
}

/**
 * Generic request helper. Attaches Authorization: Bearer <Firebase JWT>.
 * On 401 it retries exactly once with a forced token refresh to recover
 * from the "token expired between request build and API verify" race.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const token = await getIdToken(forceRefresh);
    const multipart = options.body instanceof FormData;
    const headers: Record<string, string> = multipart ? {} : {
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: options.body == null ? undefined : multipart ? options.body as FormData : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let res = await send(false);
  if (res.status === 401) {
    res = await send(true);
  }

  if (!res.ok) {
    let detail: unknown = undefined;
    let code: string | undefined = undefined;
    try {
      const data = await res.json();
      detail = data?.detail ?? data;
      // `code` sits at the top level when FastAPI's HTTPException wrapped the
      // body, and inside `detail` for the structured conflict payloads. Check
      // both, outer first — same precedence as prospin-web's client.ts.
      code = pickCode(data) ?? pickCode(detail);
    } catch {
      // non-JSON error body — ignore
    }
    const msg = messageFromDetail(detail) ?? `Request failed: ${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg, code, detail);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── Typed endpoints ────────────────────────────────────────────────

interface UserEnvelope {
  user: AdminUser;
}

interface SyncUserBody {
  firebase_uid: string;
  email: string | null;
  display_name: string | null;
}

/**
 * Find-or-create the API's `users` row for the currently signed-in Firebase
 * user. Idempotent — call on every bootstrap. The API requires this before
 * `/users/me` returns anything for a brand-new Firebase identity, since
 * Google sign-in alone does not create a DB row.
 *
 * Auth is the firebase_uid itself in the request body (this endpoint does
 * not require a Bearer token), so we still attach one via `request` for
 * uniformity — the API ignores it on this path.
 */
export async function syncUser(body: SyncUserBody, signal?: AbortSignal): Promise<AdminUser> {
  const res = await request<UserEnvelope>('/users/sync', { method: 'POST', body, signal });
  return res.user;
}

/**
 * Fetch the current admin's profile. Requires the row to already exist —
 * use `syncUser` first on initial sign-in.
 */
export async function getMe(signal?: AbortSignal): Promise<AdminUser> {
  const res = await request<UserEnvelope>('/users/me', { signal });
  return res.user;
}

// ─── Bookings ───────────────────────────────────────────────────────

interface BookingsEnvelope {
  bookings: Booking[];
}

/** Confirmed bookings for a given date (YYYY-MM-DD), Asia/Tbilisi. */
export async function getBookingsForDate(date: string, signal?: AbortSignal): Promise<Booking[]> {
  const res = await request<BookingsEnvelope>(`/admin/bookings/${encodeURIComponent(date)}`, { signal });
  return res.bookings;
}

/** Inspect the active share link for a booking. */
export async function getBookingShare(bookingId: number): Promise<AdminShareLink> {
  return request<AdminShareLink>(`/admin/bookings/${bookingId}/share`);
}

/** Permanently revoke a booking's share link. */
export async function revokeBookingShare(bookingId: number): Promise<void> {
  return request<void>(`/admin/bookings/${bookingId}/share`, { method: 'DELETE' });
}

/** Cancel a booking (any payment_type). Returns the cancelled row. */
export async function cancelBooking(bookingId: number): Promise<unknown> {
  return request<unknown>(`/admin/bookings/${bookingId}/cancel`, { method: 'POST' });
}

/**
 * Shorten a coach_block booking (PATCH /admin/bookings/{id}/times).
 * Times are "HH:MM". Server enforces no-overlap and coach_block-only.
 */
export async function shortenBookingTimes(
  bookingId: number,
  start_time: string,
  end_time: string,
): Promise<unknown> {
  return request<unknown>(`/admin/bookings/${bookingId}/times`, {
    method: 'PATCH',
    body: { start_time, end_time } satisfies UpdateBookingTimesBody,
  });
}

/**
 * Reschedule any confirmed booking to a new start time on the same day
 * (same table). Duration is preserved server-side; the end time is derived.
 * Staff may reschedule bookings they don't own (admin override on the shared
 * POST /bookings/{id}/transfer endpoint). Server enforces not-started,
 * bookable-now, ±12h window, and no-overlap; a 409 carries the structured
 * conflict detail (with occupant names, since the caller is staff).
 */
export async function rescheduleBooking(
  bookingId: number,
  start_time: string,
  table_id: number,
): Promise<{ booking: Booking }> {
  return request<{ booking: Booking }>(`/bookings/${bookingId}/transfer`, {
    method: 'POST',
    body: { table_id, start_time } satisfies TransferBookingBody,
  });
}

// ─── Tables (public read; admin endpoints land in a later plan) ────

interface TablesEnvelope {
  tables: TableEmbed[];
}

export async function listTables(signal?: AbortSignal): Promise<TableEmbed[]> {
  const res = await request<TablesEnvelope>('/tables', { signal });
  return res.tables;
}

export async function listCoachProfiles(signal?: AbortSignal): Promise<CoachProfile[]> {
  const response = await request<CoachProfilesResponse>('/admin/coach-profiles', { signal });
  return response.profiles;
}

export async function saveCoachProfile(
  profileId: string,
  body: CoachProfileSaveBody,
): Promise<CoachProfile> {
  return request<CoachProfile>(`/admin/coach-profiles/${encodeURIComponent(profileId)}`, {
    method: 'PUT',
    body,
  });
}

export async function listCoachCallbacks(
  options: CoachCallbackListParams = {},
  signal?: AbortSignal,
): Promise<CoachCallbacksResponse> {
  const params = new URLSearchParams();
  if (options.status !== undefined) params.set('status', options.status);
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  const query = params.size > 0 ? `?${params.toString()}` : '';
  return request<CoachCallbacksResponse>(`/admin/coach-callbacks${query}`, { signal });
}

export async function markCoachCallbackContacted(requestId: string): Promise<CoachCallback> {
  return request<CoachCallback>(
    `/admin/coach-callbacks/${encodeURIComponent(requestId)}`,
    { method: 'PATCH', body: { status: 'contacted' } satisfies CoachCallbackContactBody },
  );
}

// ─── Coach series ──────────────────────────────────────────────────

interface CoachSeriesListEnvelope {
  series: CoachSeries[];
}

export type CoachSeriesActiveFilter = 'true' | 'false' | 'all';

export async function listCoachSeries(
  opts: { coach_user_id?: string; table_id?: number; active?: CoachSeriesActiveFilter } = {},
  signal?: AbortSignal,
): Promise<CoachSeries[]> {
  const params = new URLSearchParams();
  if (opts.coach_user_id) params.set('coach_user_id', opts.coach_user_id);
  if (opts.table_id !== undefined) params.set('table_id', String(opts.table_id));
  if (opts.active) params.set('active', opts.active);
  const q = params.toString();
  const path = '/admin/coach-series' + (q ? `?${q}` : '');
  const res = await request<CoachSeriesListEnvelope>(path, { signal });
  return res.series;
}

export async function getCoachSeries(
  id: number,
  signal?: AbortSignal,
): Promise<CoachSeriesDetail> {
  return request<CoachSeriesDetail>(`/admin/coach-series/${id}`, { signal });
}

export interface CreateCoachSeriesBody {
  coach_user_id: string;
  table_id: number;
  day_of_week: number;     // 0 = Monday
  start_time: string;      // "HH:MM"
  end_time: string;        // "HH:MM"
  starts_on: string;       // YYYY-MM-DD
  ends_on?: string | null; // YYYY-MM-DD or null/omit for open-ended
}

interface CreateCoachSeriesResponse {
  series: CoachSeries;
  occurrences_inserted: number;
}

export async function createCoachSeries(
  body: CreateCoachSeriesBody,
): Promise<CreateCoachSeriesResponse> {
  return request<CreateCoachSeriesResponse>('/admin/coach-series', {
    method: 'POST',
    body,
  });
}

/**
 * A single off-cycle coach session — trial lesson, make-up, substitute slot
 * (`POST /admin/coach-series/one-time`, givip/prospin-api#83).
 *
 * Hand-typed like the rest of the coach-series family: the committed
 * `openapi.json` is pinned to prospin-api's `main` and predates this endpoint
 * (see .ai/features/47-one-time-coach-booking/DECISIONS.md).
 *
 * `date` is a venue-local YYYY-MM-DD and the times are venue-local "HH:MM"
 * (`24:00` allowed as the end-of-day boundary). Requires an api built from
 * `epic/booking-editing` or later.
 */
export interface CreateOneTimeCoachSessionBody {
  coach_user_id: string;
  table_id: number;
  date: string;
  start_time: string;
  end_time: string;
}

export interface CreateOneTimeCoachSessionResponse {
  /** The series of length 1 the session is stored as — `is_one_time: true`. */
  series: CoachSeries;
  /** The single `coach_block` booking it materialized. */
  booking: OneTimeCoachBooking;
}

export async function createOneTimeCoachSession(
  body: CreateOneTimeCoachSessionBody,
): Promise<CreateOneTimeCoachSessionResponse> {
  return request<CreateOneTimeCoachSessionResponse>('/admin/coach-series/one-time', {
    method: 'POST',
    body,
  });
}

export async function patchCoachSeries(
  id: number,
  body: { ends_on: string | null },
): Promise<CoachSeries> {
  return request<CoachSeries>(`/admin/coach-series/${id}`, {
    method: 'PATCH',
    body,
  });
}

/**
 * Move a whole series to a new same-day window, in place
 * (`PATCH /admin/coach-series/{id}/time`, givip/prospin-api#84).
 *
 * The series keeps its identity, coach, table, weekday and date range; only the
 * daily window moves, and only for occurrences that have not started yet.
 * Already-elapsed, individually-cancelled and manually-re-timed ones are left
 * alone and come back in `skipped` with a reason.
 *
 * `dryRun` runs the identical server-side code path *without writing*, so the
 * UI previews exactly what will move before staff confirm — the endpoint's own
 * mechanism, not a client simulation. Call it once to preview, then again with
 * the same body and no `dryRun` to commit.
 *
 * The shift is all-or-nothing: one colliding occurrence rejects the whole thing
 * with a 409 carrying `SeriesOccurrenceConflictDetail` (narrow it with
 * `seriesOccurrenceConflictDetail`). The window rules — same-day, minimum
 * duration, slot alignment — are the API's and surface as a 400 whose message
 * is meant to be shown verbatim.
 */
export async function shiftCoachSeriesTime(
  id: number,
  body: ShiftCoachSeriesTimeBody,
  opts: { dryRun?: boolean } = {},
): Promise<SeriesTimeShiftResult> {
  // The write path is the *absence* of the flag, which is what the endpoint
  // documents — don't send `dry_run=false`.
  const path = `/admin/coach-series/${id}/time` + (opts.dryRun ? '?dry_run=true' : '');
  return request<SeriesTimeShiftResult>(path, {
    method: 'PATCH',
    body: body satisfies ShiftCoachSeriesTimeBody,
  });
}

/**
 * Move a whole series to a different table, in place
 * (`PATCH /admin/coach-series/{id}/table`, givip/prospin-api#85).
 *
 * The series keeps its identity, coach, times, weekday cadence and date range;
 * only the table moves, and only for occurrences that have not started yet.
 * Already-elapsed, individually-cancelled and individually-relocated ones are
 * left alone and come back in `skipped` with a reason. Times are never touched,
 * so an occurrence somebody re-timed by hand travels along with its own window
 * — which is why each `moved` entry carries its own times rather than the
 * series'.
 *
 * `dryRun` / commit work exactly as in `shiftCoachSeriesTime`: preview with the
 * flag, then send the same body without it to write.
 *
 * All-or-nothing: one colliding occurrence rejects the whole transfer with a
 * 409 carrying `SeriesOccurrenceConflictDetail` (narrow it with
 * `seriesOccurrenceConflictDetail`). Target-table rules are the API's — "Table
 * not found", "Table is inactive" and "Series is already on that table" arrive
 * as 400s whose message is meant to be shown verbatim, so don't pre-empt them.
 */
export async function transferCoachSeriesTable(
  id: number,
  body: TransferCoachSeriesTableBody,
  opts: { dryRun?: boolean } = {},
): Promise<SeriesTableTransferResult> {
  // The write path is the *absence* of the flag — see shiftCoachSeriesTime.
  const path = `/admin/coach-series/${id}/table` + (opts.dryRun ? '?dry_run=true' : '');
  return request<SeriesTableTransferResult>(path, {
    method: 'PATCH',
    body: body satisfies TransferCoachSeriesTableBody,
  });
}

export async function deleteCoachSeries(id: number): Promise<CoachSeries> {
  return request<CoachSeries>(`/admin/coach-series/${id}`, {
    method: 'DELETE',
  });
}

// ─── Users: lookup + coach toggle ──────────────────────────────────

interface AdminUserEnvelope {
  user: AdminLookupUser;
}

export async function findUserByPhone(
  phone: string,
  signal?: AbortSignal,
): Promise<AdminLookupUser> {
  const res = await request<AdminUserEnvelope>(
    `/admin/users/by-phone/${encodeURIComponent(phone)}`,
    { signal },
  );
  return res.user;
}

export async function createUser(
  body: {
    phone: string;
    first_name: string;
    last_name?: string;
    is_coach: boolean;
    credit_minutes: number;
  },
  signal?: AbortSignal,
): Promise<AdminLookupUser> {
  const res = await request<AdminUserEnvelope>('/admin/users', {
    method: 'POST',
    body,
    signal,
  });
  return res.user;
}

export async function setCoachRole(
  userId: string,
  isCoach: boolean,
): Promise<AdminLookupUser> {
  return request<AdminLookupUser>(`/admin/users/${userId}/coach`, {
    method: 'PATCH',
    body: { is_coach: isCoach } satisfies ToggleCoachBody,
  });
}

// ─── Doors ─────────────────────────────────────────────────────────

export type DoorWhich = 'outside' | 'inside';

export async function openDoor(which: DoorWhich): Promise<unknown> {
  return request<unknown>(`/admin/doors/${which}/open`, { method: 'POST' });
}

// ─── Hall climate ──────────────────────────────────────────────────

/** Current hall readings, AC state, and observed automation-policy state. */
export async function getClimate(signal?: AbortSignal): Promise<ClimateSnapshot> {
  return request<ClimateSnapshot>('/admin/climate', { signal });
}

/** Request Auto Climate's desired state; the response is the device read-back. */
export async function setAutoClimate(on: boolean): Promise<ClimateSwitchState> {
  const body = { on } satisfies SetClimateSwitchBody;
  return request<ClimateSwitchState>('/admin/climate/auto-climate', { method: 'POST', body });
}

/** Request Night Mode's desired state; the response is the device read-back. */
export async function setNightMode(on: boolean): Promise<ClimateSwitchState> {
  const body = { on } satisfies SetClimateSwitchBody;
  return request<ClimateSwitchState>('/admin/climate/night-mode', { method: 'POST', body });
}

// ─── Hall streams ──────────────────────────────────────────────────

/** Current observed stream state from the hall NUC. */
export async function listStreams(signal?: AbortSignal): Promise<StreamsResponse> {
  return request<StreamsResponse>('/admin/streams', { signal });
}

/** Start one configured camera stream. The page refreshes observed status after this succeeds. */
export async function startStream(channel: number): Promise<StreamActionResponse> {
  return request<StreamActionResponse>(`/admin/streams/${channel}/start`, { method: 'POST' });
}

/** Stop one live or preview camera stream. */
export async function stopStream(channel: number): Promise<StreamActionResponse> {
  return request<StreamActionResponse>(`/admin/streams/${channel}/stop`, { method: 'POST' });
}

/** Ask the controller to stop all streams in one action. */
export async function stopAllStreams(): Promise<StopAllStreamsResponse> {
  return request<StopAllStreamsResponse>('/admin/streams/stop-all', { method: 'POST' });
}

/** The slow, on-demand NVR channel enumeration. */
export async function listStreamCameras(signal?: AbortSignal): Promise<NvrCamerasResponse> {
  return request<NvrCamerasResponse>('/admin/streams/cameras', { signal });
}

/** Server-owned tournament eligibility, integration readiness, and saved broadcast results. */
export async function getTournamentYoutubeBroadcasts(
  tournamentId: number,
  signal?: AbortSignal,
): Promise<TournamentYoutubeBroadcastsResponse> {
  return request<TournamentYoutubeBroadcastsResponse>(
    `/admin/streams/youtube/tournaments/${tournamentId}`,
    { signal },
  );
}

/** Create or safely resume the selected table's server-derived broadcast. */
export async function createTournamentYoutubeBroadcasts(
  tournamentId: number,
  options: CreateTournamentYoutubeBroadcastOptions,
  signal?: AbortSignal,
): Promise<TournamentYoutubeBroadcastsResponse> {
  return request<TournamentYoutubeBroadcastsResponse>(
    `/admin/streams/youtube/tournaments/${tournamentId}/create`,
    { method: 'POST', body: { table_id: options.tableId }, signal },
  );
}

// ─── Club opening hours ────────────────────────────────────────────

/** Full weekly schedule for the club-hours editor. */
export async function getOpeningHours(signal?: AbortSignal): Promise<OpeningHours> {
  return request<OpeningHours>('/admin/club/opening-hours', { signal });
}

/** Atomically replace all seven weekday rules and receive any affected bookings. */
export async function putOpeningHours(days: OpeningHoursDay[]): Promise<OpeningHours> {
  // The response adds `closes_next_day`, which is deliberately not sent back
  // to the Pydantic input model. Keep the payload to the generated component's
  // exact four fields even when callers round-trip a loaded schedule.
  const body = {
    days: days.map(({ day_of_week, is_closed, open_time, close_time }) => ({
      day_of_week,
      is_closed,
      open_time,
      close_time,
    })),
  } satisfies OpeningHoursPutBody;
  return request<OpeningHours>('/admin/club/opening-hours', { method: 'PUT', body });
}

export async function listMaintenanceWindows(signal?: AbortSignal): Promise<MaintenanceWindow[]> {
  const res = await request<{ windows: MaintenanceWindow[] }>('/admin/club/maintenance-windows', {
    signal,
  });
  return res.windows;
}

export async function createMaintenanceWindow(
  body: CreateMaintenanceWindowBody,
): Promise<{ window: MaintenanceWindow; affected_bookings: AffectedBooking[] }> {
  return request('/admin/club/maintenance-windows', { method: 'POST', body });
}

export async function deleteMaintenanceWindow(id: number): Promise<void> {
  return request<void>(`/admin/club/maintenance-windows/${id}`, { method: 'DELETE' });
}

// ─── Paired lobby displays ────────────────────────────────────────

interface DisplayDeviceEnvelope {
  device: DisplayDevice;
}

interface DisplayDeviceListEnvelope {
  devices: DisplayDevice[];
}

/** Safe admin metadata only: no credential or credential hash is in this contract. */
export async function listDisplayDevices(signal?: AbortSignal): Promise<DisplayDevice[]> {
  const res = await request<DisplayDeviceListEnvelope>('/admin/display-devices', { signal });
  return res.devices;
}

/** Approve an active pairing code; the paired TV receives its own credential out of band. */
export async function approveDisplayPairing(
  body: ApproveDisplayPairingBody,
): Promise<DisplayDevice> {
  const res = await request<DisplayDeviceEnvelope>('/admin/display-devices/approve', {
    method: 'POST',
    body: body satisfies ApproveDisplayPairingBody,
  });
  return res.device;
}

/** Change only the staff-visible label of one display device. */
export async function renameDisplayDevice(
  deviceId: string,
  body: UpdateDisplayDeviceBody,
): Promise<DisplayDevice> {
  const res = await request<DisplayDeviceEnvelope>(
    `/admin/display-devices/${encodeURIComponent(deviceId)}`,
    { method: 'PATCH', body: body satisfies UpdateDisplayDeviceBody },
  );
  return res.device;
}

/** Remove one display device and sign its TV out. */
export async function deleteDisplayDevice(deviceId: string): Promise<void> {
  return request<void>(`/admin/display-devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
}

// ─── Users directory + subscriptions ───────────────────────────────

interface UsersListResponse {
  users: AdminLookupUser[];
  total: number;
  limit: number;
  offset: number;
}

export async function listUsers(
  opts: { q?: string; limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<Paged<AdminLookupUser>> {
  const params = new URLSearchParams();
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const q = params.toString();
  const path = '/admin/users' + (q ? `?${q}` : '');
  const res = await request<UsersListResponse>(path, { signal });
  return { items: res.users, total: res.total, limit: res.limit, offset: res.offset };
}

interface AdminUserDetailEnvelope {
  user: AdminLookupUser;
}

interface TtarLinkStateEnvelope {
  ttar: TtarLinkState;
}

export async function getAdminUser(id: string, signal?: AbortSignal): Promise<AdminLookupUser> {
  const res = await request<AdminUserDetailEnvelope>(
    `/admin/users/${encodeURIComponent(id)}`,
    { signal },
  );
  return res.user;
}

/** Read a user's TTAR identity and provenance without exposing OAuth secrets. */
export async function getAdminTtarLink(
  userId: string,
  signal?: AbortSignal,
): Promise<TtarLinkState> {
  const res = await request<TtarLinkStateEnvelope>(
    `/admin/users/${encodeURIComponent(userId)}/ttar`,
    { signal },
  );
  return res.ttar;
}

/**
 * Attach a TTAR ID, or replace it when the caller includes explicit confirmation.
 * The caller controls the exact body so an initial attachment sends only the ID.
 */
export async function setAdminTtarLink(
  userId: string,
  body: AdminTtarLinkBody,
): Promise<TtarLinkState> {
  const res = await request<TtarLinkStateEnvelope>(
    `/admin/users/${encodeURIComponent(userId)}/ttar`,
    { method: 'PUT', body },
  );
  return res.ttar;
}

/** Remove a TTAR ID. This wrapper always sends the API's required confirmation. */
export async function removeAdminTtarLink(userId: string): Promise<TtarLinkState> {
  const res = await request<TtarLinkStateEnvelope>(
    `/admin/users/${encodeURIComponent(userId)}/ttar?confirm=true`,
    { method: 'DELETE' },
  );
  return res.ttar;
}

export interface PatchUserBody {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  is_admin?: boolean;
}

export async function patchUser(id: string, body: PatchUserBody): Promise<AdminLookupUser> {
  const res = await request<AdminUserDetailEnvelope>(
    `/admin/users/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
  );
  return res.user;
}

interface UserBookingsResponse {
  bookings: Booking[];
  total: number;
  limit: number;
  offset: number;
}

export async function getUserBookings(
  userId: string,
  opts: { limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<Paged<Booking>> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const path = `/admin/users/${encodeURIComponent(userId)}/bookings` + (qs ? `?${qs}` : '');
  const res = await request<UserBookingsResponse>(path, { signal });
  return { items: res.bookings, total: res.total, limit: res.limit, offset: res.offset };
}

interface SubsListEnvelope {
  subscriptions: SubscriptionWithUsage[];
}

export async function getUserSubscriptions(
  userId: string,
  signal?: AbortSignal,
): Promise<SubscriptionWithUsage[]> {
  const res = await request<SubsListEnvelope>(
    `/admin/users/${encodeURIComponent(userId)}/subscriptions`,
    { signal },
  );
  return res.subscriptions;
}

export interface IssueSubscriptionBody {
  plan: SubscriptionPlan;
  hours_total: number;
  starts_at?: string;       // ISO 8601; default = now (server-side)
  expires_at: string;       // ISO 8601
  payment_id?: number | null;
}

export async function issueSubscription(
  userId: string,
  body: IssueSubscriptionBody,
): Promise<SubscriptionWithUsage> {
  return request<SubscriptionWithUsage>(
    `/admin/users/${encodeURIComponent(userId)}/subscriptions`,
    { method: 'POST', body },
  );
}

export async function patchSubscription(
  id: number,
  body: { expires_at?: string; status?: 'cancelled' },
): Promise<SubscriptionWithUsage> {
  return request<SubscriptionWithUsage>(`/admin/subscriptions/${id}`, {
    method: 'PATCH',
    body,
  });
}

/**
 * Edit a user's credit balance via /admin/users/{id}/credit. `minutes` is a
 * signed delta: positive adds, negative removes (the API floors the balance at
 * 0). Magnitude must be 1–12000 and non-zero.
 */
export async function editCredit(
  userId: string,
  minutes: number,
): Promise<{ user_id: string; minutes_added: number; new_balance_minutes: number }> {
  return request(
    `/admin/users/${encodeURIComponent(userId)}/credit?minutes=${minutes}`,
    { method: 'POST' },
  );
}

// ─── Tables admin CRUD ─────────────────────────────────────────────

export interface CreateTableBody {
  name: string;
  display_order: number;
  description?: string | null;
  page_url?: string | null;
  hourly_rate?: number | null;
  is_active?: boolean;
  images?: string[];
}

export type PatchTableBody = Partial<Omit<CreateTableBody, 'name'>> & { name?: string };

export async function createTable(body: CreateTableBody): Promise<AdminTable> {
  return request<AdminTable>('/admin/tables', { method: 'POST', body });
}

export async function patchTable(id: number, body: PatchTableBody): Promise<AdminTable> {
  return request<AdminTable>(`/admin/tables/${id}`, { method: 'PATCH', body });
}

interface AdminTablesEnvelope {
  tables: AdminTable[];
}

/** Full table rows (active + inactive) for the admin panel. */
export async function getAdminTables(signal?: AbortSignal): Promise<AdminTable[]> {
  const res = await request<AdminTablesEnvelope>('/admin/tables', { signal });
  return res.tables;
}

// ─── Vending admin catalog and purchase ledger ─────────────────────

interface VendingProductsEnvelope {
  products: AdminVendingProduct[];
}

export interface GetVendingPurchasesParams {
  from: string;
  to: string;
  limit: number;
  offset: number;
}

/** Multipart upload: the browser supplies the boundary and the shared client attaches auth. */
export async function uploadVendingImage(file: File): Promise<components['schemas']['VendingImageUploadResponse']> {
  const body = new FormData();
  body.append('file', file);
  return request('/admin/vending/images', { method: 'POST', body });
}

/** All catalog products, including archived rows, for staff management. */
export async function getVendingProducts(signal?: AbortSignal): Promise<AdminVendingProduct[]> {
  const res = await request<VendingProductsEnvelope>('/admin/vending/products', { signal });
  return res.products;
}

export async function createVendingProduct(body: CreateVendingProductBody): Promise<AdminVendingProduct> {
  return request<AdminVendingProduct>('/admin/vending/products', { method: 'POST', body });
}

export async function patchVendingProduct(
  id: string,
  body: PatchVendingProductBody,
): Promise<AdminVendingProduct> {
  return request<AdminVendingProduct>(`/admin/vending/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export async function getVendingPurchases(
  { from, to, limit, offset }: GetVendingPurchasesParams,
  signal?: AbortSignal,
): Promise<VendingPurchasesResponse> {
  const params = new URLSearchParams({ from, to, limit: String(limit), offset: String(offset) });
  return request<VendingPurchasesResponse>(`/admin/vending/purchases?${params.toString()}`, { signal });
}

// ─── Settled sales timeline ─────────────────────────────────────────

export interface ListSalesParams {
  /** Omit for all purchase types; the API applies filtering before pagination. */
  kind?: SalesKind;
  limit?: number;
  offset?: number;
}

/**
 * Read the admin sales timeline. `operation` and signed `amount_minor` come
 * straight from the API contract so a future completed refund needs no client
 * side payment/refund inference.
 */
export async function listSales(
  { kind, limit, offset }: ListSalesParams = {},
  signal?: AbortSignal,
): Promise<AdminSalesResponse> {
  const params = new URLSearchParams();
  if (kind !== undefined) params.set('kind', kind);
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  const query = params.toString();
  return request<AdminSalesResponse>(`/admin/sales${query ? `?${query}` : ''}`, { signal });
}

// ─── Manual booking creation ───────────────────────────────────────

export interface CreateBookingBody {
  user_id: string;
  table_id: number;
  date: string;          // YYYY-MM-DD
  start_time: string;    // HH:MM
  end_time: string;      // HH:MM
  // Neither block type is bookable here: a coach_block belongs to a coach
  // series and a tournament_block to a tournament, and both are created by
  // their own endpoints. Widening BookingPaymentType must not widen this.
  payment_type: Exclude<BookingPaymentType, 'coach_block' | 'tournament_block'>;
  subscription_id?: number | null;
  payment_id?: number | null;
  billable_minutes?: number | null;
}

export async function createBooking(body: CreateBookingBody): Promise<Booking> {
  return request<Booking>('/admin/bookings', { method: 'POST', body });
}

// ─── Tournaments (one-off, admin#66) ────────────────────────────────
//
// Depends on prospin-api PRs for issues #153 (typed admin responses),
// #154 (en/ru/ka `translations`) and #155 (PATCH + publish + structured
// conflicts) — see .ai/features (or the epic tracker) for status. Every
// freeze/capacity/conflict decision below is the API's: this file only
// carries the request/response shapes, never re-derives the rule.

interface TournamentDetailEnvelope {
  tournament: AdminTournament;
}

interface TournamentListEnvelope {
  tournaments: AdminTournament[];
}

export async function listTournaments(signal?: AbortSignal): Promise<AdminTournament[]> {
  const res = await request<TournamentListEnvelope>('/admin/tournaments', { signal });
  return res.tournaments;
}

export async function getTournament(id: number, signal?: AbortSignal): Promise<AdminTournament> {
  const res = await request<TournamentDetailEnvelope>(`/admin/tournaments/${id}`, { signal });
  return res.tournament;
}

/**
 * Read the provider-owned TTAR mapping and safe enrollment retry state. URLs,
 * organizer credentials, and provider tokens intentionally never reach this
 * client; the API chooses the projector URL for the active booking window.
 */
export async function getTtarTournamentSync(
  id: number,
  signal?: AbortSignal,
): Promise<TtarTournamentSyncResponse> {
  return request<TtarTournamentSyncResponse>(`/admin/tournaments/${id}/ttar/sync`, { signal });
}

/**
 * Ask the API for one bounded TTAR create/enrollment pass. The request has no
 * browser-supplied retry count or provider data: durable server state remains
 * authoritative and may report that its retry limit has been exhausted.
 */
export async function runTtarTournamentSync(id: number): Promise<TtarTournamentSyncResponse> {
  return request<TtarTournamentSyncResponse>(`/admin/tournaments/${id}/ttar/sync`, {
    method: 'POST',
  });
}

/**
 * Reset one player's TTAR enrollment attempts and run one attempt now. The API
 * refuses (409) an enrolled row or one with an attempt still running; a TTAR
 * failure is not an error — the returned enrollment row carries its new
 * status and `last_error`.
 */
export async function retryTtarEnrollment(
  tournamentId: number,
  userId: string,
): Promise<TtarTournamentSyncResponse> {
  return request<TtarTournamentSyncResponse>(
    `/admin/tournaments/${tournamentId}/ttar/enrollments/${encodeURIComponent(userId)}/retry`,
    { method: 'POST' },
  );
}

/** Always created as `draft` (or explicit `status: 'published'`) — see CreateTournamentBody. */
export async function createTournament(body: CreateTournamentBody): Promise<AdminTournament> {
  const res = await request<TournamentDetailEnvelope>('/admin/tournaments', {
    method: 'POST',
    body: body satisfies CreateTournamentBody,
  });
  return res.tournament;
}

/**
 * Edit a tournament (api#155). Only fields present in `body` are applied —
 * omit a field to leave it untouched, send `null` where the schema allows it
 * to clear it (e.g. `image_url`).
 *
 * Before the first confirmed participant every field here is editable; after,
 * the schedule/tables/fee/deadline are frozen and the API rejects a patch that
 * touches any of them with a 409 `frozen_field` — carrying no `conflicts[]`,
 * just `error`/`code`, so it surfaces through `ApiError.message` like any other
 * coded rejection. A schedule/table conflict (`overlap`) does carry the full
 * `conflicts[]`/`next_free_time` shape and narrows with `slotConflictDetail`.
 */
export async function patchTournament(
  id: number,
  body: PatchTournamentBody,
): Promise<AdminTournament> {
  const res = await request<TournamentDetailEnvelope>(`/admin/tournaments/${id}`, {
    method: 'PATCH',
    body: body satisfies PatchTournamentBody,
  });
  return res.tournament;
}

/** `draft` → `published`. A 409 `not_draft` covers every other current status. */
export async function publishTournament(id: number): Promise<AdminTournament> {
  const res = await request<TournamentDetailEnvelope>(`/admin/tournaments/${id}/publish`, {
    method: 'POST',
  });
  return res.tournament;
}

interface TournamentCancelResponse {
  tournament: AdminTournament;
  removed_participants: number;
}

/** Frees the blocked tables and clears the roster, in one transaction. */
export async function cancelTournament(id: number): Promise<TournamentCancelResponse> {
  return request<TournamentCancelResponse>(`/admin/tournaments/${id}/cancel`, {
    method: 'POST',
  });
}

interface AdminParticipantsEnvelope {
  participants: AdminParticipant[];
}

/** R5 — the roster: who registered, their status, and their payment state. */
export async function listTournamentParticipants(
  id: number,
  signal?: AbortSignal,
): Promise<AdminParticipant[]> {
  const res = await request<AdminParticipantsEnvelope>(
    `/admin/tournaments/${id}/participants`,
    { signal },
  );
  return res.participants;
}

interface ParticipantRemovalEnvelope {
  participant: ParticipantRecord;
}

/** Removes a participant, freeing their spot. No refund action of any kind. */
/** Staff received the cash for a pending cash seat — settle fee + confirm
 *  the seat. 409 `no_pending_cash_payment` when the seat's fee is a card
 *  payment or already settled. */
export async function confirmTournamentCashPayment(
  id: number,
  userId: string,
): Promise<ParticipantRecord> {
  const res = await request<{ participant: ParticipantRecord }>(
    `/admin/tournaments/${id}/participants/${encodeURIComponent(userId)}/confirm-cash`,
    { method: 'POST' },
  );
  return res.participant;
}

export async function removeTournamentParticipant(
  id: number,
  userId: string,
): Promise<ParticipantRecord> {
  const res = await request<ParticipantRemovalEnvelope>(
    `/admin/tournaments/${id}/participants/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
  return res.participant;
}

// ─── Tournament series (recurring tournaments, admin#67) ────────────
//
// Depends on prospin-api's tournament recurrence slice (api#156) — see
// .ai/features/156-tournament-recurrence (DECISIONS.md) for the contract.
// Every conflict/freeze/eligibility decision below is the API's: this file
// only carries the request/response shapes, never re-derives a rule.

interface TournamentSeriesListEnvelope {
  series: AdminTournamentSeries[];
}

export type TournamentSeriesActiveFilter = 'true' | 'false' | 'all';

export async function listTournamentSeries(
  active: TournamentSeriesActiveFilter = 'true',
  signal?: AbortSignal,
): Promise<AdminTournamentSeries[]> {
  const res = await request<TournamentSeriesListEnvelope>(
    `/admin/tournament-series?active=${active}`,
    { signal },
  );
  return res.series;
}

export async function getTournamentSeries(
  id: number,
  signal?: AbortSignal,
): Promise<AdminTournamentSeriesDetailResponse> {
  return request<AdminTournamentSeriesDetailResponse>(`/admin/tournament-series/${id}`, {
    signal,
  });
}

/**
 * Create a recurring tournament series and bootstrap its in-horizon
 * occurrences. Always resolves (201) even when some dates conflict — the
 * API skips a conflicting date and reports it rather than aborting the
 * whole create (api#156's deliberate deviation from `coach_series`'
 * all-or-nothing bootstrap). Callers render `occurrences_created` /
 * `occurrences_skipped` from the response; there is no 409 path to catch
 * for a scheduling conflict here.
 */
export async function createTournamentSeries(
  body: CreateTournamentSeriesBody,
): Promise<AdminTournamentSeriesCreateResponse> {
  return request<AdminTournamentSeriesCreateResponse>('/admin/tournament-series', {
    method: 'POST',
    body: body satisfies CreateTournamentSeriesBody,
  });
}

/**
 * Edit a series (api#156). Content fields (`fee_minor`, `max_participants`,
 * `image_url`, `translations`/`title`/`description`) apply to the rule AND
 * to every eligible already-materialized future occurrence — reported in
 * `updated_occurrences`. Schedule fields (`day_of_week`, `start_time`,
 * `end_time`, `table_ids`, `starts_on`, `ends_on`) are rule-only and never
 * retroactively touch an existing occurrence. An occurrence with any
 * participant history, that has elapsed, or isn't in `draft`/`published`
 * status is left alone and reported in `skipped_occurrences` with a reason
 * — never bulk-mutated by this call.
 */
export async function patchTournamentSeries(
  id: number,
  body: PatchTournamentSeriesBody,
): Promise<AdminTournamentSeriesEditResponse> {
  return request<AdminTournamentSeriesEditResponse>(`/admin/tournament-series/${id}`, {
    method: 'PATCH',
    body: body satisfies PatchTournamentSeriesBody,
  });
}

/**
 * Stop future materialization. Every already-materialized occurrence,
 * whatever its date, is left completely intact — nothing is cancelled
 * (deliberate deviation from `coach_series`'s end, which bulk-cancels;
 * see api#156's DECISIONS.md). Omit `ends_on` to stop immediately (the API
 * defaults it to yesterday).
 */
export async function endTournamentSeries(
  id: number,
  body: EndTournamentSeriesBody = {},
): Promise<AdminTournamentSeriesDetailResponse> {
  return request<AdminTournamentSeriesDetailResponse>(`/admin/tournament-series/${id}/end`, {
    method: 'POST',
    body: body satisfies EndTournamentSeriesBody,
  });
}

// ─── Stats ──────────────────────────────────────────────────────────

function statsPath(section: string, start: string, end: string): string {
  const params = new URLSearchParams({ start, end });
  return `/admin/stats/${section}?${params.toString()}`;
}

export async function getStatsRevenue(
  start: string,
  end: string,
  signal?: AbortSignal,
): Promise<RevenueStats> {
  return request<RevenueStats>(statsPath('revenue', start, end), { signal });
}

export async function getStatsBookings(
  start: string,
  end: string,
  signal?: AbortSignal,
): Promise<BookingStats> {
  return request<BookingStats>(statsPath('bookings', start, end), { signal });
}

export async function getStatsOccupancy(
  start: string,
  end: string,
  signal?: AbortSignal,
): Promise<OccupancyStats> {
  return request<OccupancyStats>(statsPath('occupancy', start, end), { signal });
}

export async function getStatsMembers(
  start: string,
  end: string,
  signal?: AbortSignal,
): Promise<MemberStats> {
  return request<MemberStats>(statsPath('members', start, end), { signal });
}

// Convenience re-export for ad-hoc calls during early development.
// Remove once every endpoint has a typed wrapper above.
export const api = {
  request,
  syncUser,
  getMe,
  getBookingsForDate,
  getBookingShare,
  revokeBookingShare,
  cancelBooking,
  shortenBookingTimes,
  listTables,
  getAdminTables,
  getVendingProducts,
  uploadVendingImage,
  createVendingProduct,
  patchVendingProduct,
  getVendingPurchases,
  listSales,
  patchUser,
  listCoachSeries,
  listCoachCallbacks,
  markCoachCallbackContacted,
  getCoachSeries,
  createCoachSeries,
  patchCoachSeries,
  shiftCoachSeriesTime,
  transferCoachSeriesTable,
  deleteCoachSeries,
  findUserByPhone,
  setCoachRole,
  openDoor,
  getClimate,
  setAutoClimate,
  setNightMode,
  listStreams,
  startStream,
  stopStream,
  stopAllStreams,
  listStreamCameras,
  getTournamentYoutubeBroadcasts,
  createTournamentYoutubeBroadcasts,
  listDisplayDevices,
  approveDisplayPairing,
  renameDisplayDevice,
  deleteDisplayDevice,
  listUsers,
  getAdminUser,
  getUserBookings,
  getUserSubscriptions,
  issueSubscription,
  patchSubscription,
  editCredit,
  createTable,
  patchTable,
  createBooking,
  listTournaments,
  getTournament,
  getTtarTournamentSync,
  runTtarTournamentSync,
  createTournament,
  patchTournament,
  publishTournament,
  cancelTournament,
  listTournamentParticipants,
  removeTournamentParticipant,
  confirmTournamentCashPayment,
  listTournamentSeries,
  getTournamentSeries,
  createTournamentSeries,
  patchTournamentSeries,
  endTournamentSeries,
  getStatsRevenue,
  getStatsBookings,
  getStatsOccupancy,
  getStatsMembers,
};
